import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Grid } from "@mantine/core";
import { ColumnDef } from "@tanstack/react-table";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { SettingsFormContent, SettingsFormCTAs } from ".";
import {
  TransportDeviceValidationSchema,
  TransportDeviceFormInput,
} from "./validation/TransportDeviceValidationSchema";
import {
  useGetTransportDevices,
  useGetTransportDeviceTypes,
  postCreateTransportDevice,
  deleteTransportDevice,
} from "@/services/LogisticsCoordinator";
import { useAuth } from "@/contexts/AuthContext";
import { useModal } from "@/contexts/ModalContext";
import { TransportDevice, TransportDeviceType } from "@/types";
import { SuccessNotificationObj, ErrorNotificationObj } from "@/utils/NotificationUtil";
import SettingsTable from "@/components/molecules/SettingsTable/SettingsTable";
import Heading from "../../atoms/Heading";
import Stack from "../../atoms/Stack";
import TextInput from "../../atoms/TextInput";
import Select from "../../atoms/Select";
import Checkbox from "../../atoms/Checkbox";
import CTA from "../../atoms/CTA";
import Group from "../../atoms/Group";

export default function TransportDevices() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { currentCompany, isAdminUser } = useAuth();
  const companyId = currentCompany?.companyId as number;
  const isAdmin = isAdminUser(currentCompany);

  const { openModal, closeModal } = useModal();

  const { data: devices } = useGetTransportDevices({ enabled: !!companyId });
  const { data: deviceTypes, isLoading: isLoadingDeviceTypes } = useGetTransportDeviceTypes();

  const deviceTypeLabel = (deviceType: TransportDeviceType) =>
    t(`enumerables:transportDeviceTypesById.${deviceType.id}`, { defaultValue: deviceType.name });

  const energySourceLabel = (energySource: string) =>
    t(`enumerables:energySources.${energySource}`, { defaultValue: energySource });

  const deviceTypeOptions = (deviceTypes ?? []).map((type) => ({
    value: String(type.id),
    label: deviceTypeLabel(type),
  }));

  const { handleSubmit, control, reset } = useForm<TransportDeviceFormInput>({
    resolver: zodResolver(TransportDeviceValidationSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: {
      name: "",
      deviceTypeId: "",
      hasBuiltInScale: false,
    },
  });

  const { mutate: mutateCreate, isPending } = useMutation({
    mutationFn: (data: TransportDeviceFormInput) =>
      postCreateTransportDevice({
        name: data.name,
        deviceTypeId: Number(data.deviceTypeId),
        hasBuiltInScale: data.hasBuiltInScale,
        companyId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getTransportDevices"] });
      reset();
      notifications.show(
        SuccessNotificationObj(t("notifications:generic.success"), t("notifications:transportDevice.createSuccess"))
      );
    },
    onError: () =>
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.error"), t("notifications:transportDevice.createError"))
      ),
  });

  const { mutate: mutateDelete } = useMutation({
    mutationFn: (id: number) => deleteTransportDevice(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getTransportDevices"] });
      closeModal();
      notifications.show(
        SuccessNotificationObj(t("notifications:generic.success"), t("notifications:transportDevice.deleteSuccess"))
      );
    },
    onError: () => {
      closeModal();
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.error"), t("notifications:transportDevice.deleteError"))
      );
    },
  });

  const columns: ColumnDef<TransportDevice>[] = [
    { header: t("settings:transportDevices.name"), accessorKey: "name" },
    {
      id: "deviceType",
      header: t("settings:transportDevices.deviceType"),
      accessorFn: (row) => (row.deviceType ? deviceTypeLabel(row.deviceType) : "-"),
    },
    {
      id: "energySource",
      header: t("settings:transportDevices.energySource"),
      accessorFn: (row) => (row.deviceType?.energySource ? energySourceLabel(row.deviceType.energySource) : "-"),
    },
    {
      header: t("settings:transportDevices.hasBuiltInScale"),
      accessorKey: "hasBuiltInScale",
      cell: ({ getValue }) => (getValue<boolean>() ? t("common:yes") : t("common:no")),
    },
    {
      header: t("settings:transportDevices.created"),
      accessorKey: "created",
      cell: ({ getValue }) => {
        const value = getValue<string>();
        return value ? new Date(value).toLocaleDateString() : "-";
      },
    },
  ];

  const editActions = (device: TransportDevice) => [
    {
      label: t("settings:delete"),
      action: () =>
        openModal({
          title: t("actions:confirm"),
          content: t("settings:transportDevices.deleteConfirmation", { name: device.name }),
          primaryAction: {
            onClick: () => mutateDelete(device.id),
            text: t("settings:delete"),
            intent: "secondary",
          },
          secondaryAction: {
            onClick: () => closeModal(),
            text: t("actions:cancel"),
            intent: "primary",
          },
        }),
    },
  ];

  const onSubmit = (data: TransportDeviceFormInput) => mutateCreate(data);

  return (
    <Stack data-testid="transport-device-settings">
      <Heading order={2} styleOrder={4} tt="uppercase">
        {t("settings:transportDevices.title")}
      </Heading>

      <form onSubmit={handleSubmit(onSubmit)}>
        <SettingsFormContent>
          <Grid>
            <Grid.Col span={{ base: 12, xs: 6 }}>
              <Controller
                control={control}
                name="name"
                render={({ field, fieldState }) => (
                  <TextInput
                    {...field}
                    id="name"
                    withAsterisk
                    label={t("settings:transportDevices.name")}
                    value={field.value || ""}
                    error={fieldState.error?.message && t(fieldState.error.message)}
                  />
                )}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, xs: 6 }}>
              <Controller
                control={control}
                name="deviceTypeId"
                render={({ field, fieldState }) => (
                  <Select
                    {...field}
                    id="deviceTypeId"
                    withAsterisk
                    label={t("settings:transportDevices.deviceType")}
                    loading={isLoadingDeviceTypes}
                    data={deviceTypeOptions}
                    value={field.value || null}
                    onChange={(val) => field.onChange(val ?? "")}
                    clearable
                    error={fieldState.error?.message && t(fieldState.error.message)}
                  />
                )}
              />
            </Grid.Col>
            <Grid.Col span={12}>
              <Controller
                control={control}
                name="hasBuiltInScale"
                render={({ field: { value, onChange, ...field } }) => (
                  <Checkbox
                    {...field}
                    id="hasBuiltInScale"
                    label={t("settings:transportDevices.hasBuiltInScale")}
                    checked={value}
                    onChange={(event) => onChange(event.currentTarget.checked)}
                  />
                )}
              />
            </Grid.Col>
          </Grid>
        </SettingsFormContent>
        <SettingsFormCTAs>
          <Group justify="flex-end">
            <CTA intent="primary" type="submit" loading={isPending} disabled={isPending}>
              {t("actions:save")}
            </CTA>
          </Group>
        </SettingsFormCTAs>
      </form>

      <SettingsFormContent>
        <SettingsTable data={devices ?? []} columns={columns} editActions={editActions} isAdmin={isAdmin} />
      </SettingsFormContent>
    </Stack>
  );
}
